import React, { useEffect } from 'react';
import Svg from 'components/utils/Svg/Svg';
import { imageLinks } from 'assets/images';
import {
    VoiceRecorderStateData,
    VoiceRecorderActions,
    VoiceRecorderState
} from 'hooks/useVoiceRecorder';
import WaveformVisualizer from './WaveformVisualizer/WaveformVisualizer';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { useLanguage } from 'contexts/LanguageContext';
import './VoiceRecordingInterface.scss';

interface VoiceRecordingInterfaceProps {
    onSubmit: () => void;
    voiceState: VoiceRecorderStateData;
    voiceActions: VoiceRecorderActions;
    bars: number[];
}

const VoiceRecordingInterface: React.FC<VoiceRecordingInterfaceProps> = ({
    voiceState,
    voiceActions,
    bars,
    onSubmit
}) => {
    const { currentLanguage } = useLanguage();
    
    const showRecordingInterface = voiceState.currentState === VoiceRecorderState.RECORDING_AUDIO;

    const showPlaybackInterface = (voiceState.currentState !== VoiceRecorderState.IDLE && !showRecordingInterface && voiceState.audioUrl && voiceState.duration > 0);


    // Update the getTimerDisplay function
    const getTimerDisplay = () => {
        switch (voiceState.currentState) {
            case VoiceRecorderState.RECORDING_AUDIO:
            case VoiceRecorderState.RECORDING_PAUSED:
                return voiceState.formattedDuration; // Show recording duration
            case VoiceRecorderState.RECORD_PLAYING:
            case VoiceRecorderState.RECORD_PAUSED:
                return voiceState.formattedPlaybackDuration || '0:00'; // Show current playback position
            case VoiceRecorderState.IDLE:
            default:
                return voiceState.formattedDuration; // Show total recorded duration
        }
    };

    // Determine indicator state
    const getIndicatorState = () => {
        switch (voiceState.currentState) {
            case VoiceRecorderState.RECORDING_AUDIO:
                return 'recording';
            case VoiceRecorderState.RECORDING_PAUSED:
                return 'paused';
            case VoiceRecorderState.RECORD_PLAYING:
                return 'playing';
            case VoiceRecorderState.RECORD_PAUSED:
                return 'paused';
            default:
                return '';
        }
    };
    
    // Handle submit with language check
    const handleSubmit = () => {
        onSubmit();
    };

      useEffect(() => {
        console.log("currentLanguage", currentLanguage);

        if (currentLanguage === null) {
            console.log("No language trying to stop recording")
            // voiceActions.pauseRecording();
        } else {
            console.log("recording .... started")

            // Only start recording if onboarding has been shown before
            voiceActions.startRecording();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLanguage]);

    return (
        <div className="voice-recording-interface">
           
            <div className="recording-content">
                <button
                    type="button"
                    className="cancel-button"
                    onClick={(e) => {
                        // Explicit onClick handler for React Native webview compatibility
                        e.preventDefault();
                        voiceActions.cancelRecording();
                    }}
                    aria-label="Cancel recording"
                >
                    <Svg src={imageLinks.icon.greyCross} />
                </button>
                {/* Recording Timer Component - Dedicated for RECORDING_AUDIO state */}
                {!voiceState.isPaused ? (
                    <div className="recording-timer recording-active">
                        <div
                            className={`recording-indicator playing`}
                            aria-label={getIndicatorState()}
                        />
                        <div className="timer-content">
                            <span className="duration-text">
                                {getTimerDisplay()}
                            </span>
                            {/* <span className="status-text">{getStatusText()}</span> */}
                        </div>
                    </div>
                ) : (
                    <div className="recording-timer playback-active">
                        <>
                            {voiceState.currentState === VoiceRecorderState.RECORD_PLAYING ? (
                                <button
                                    type="button"
                                    className="playback-button"
                                    onClick={(e) => {
                                        // Explicit onClick handler for React Native webview compatibility
                                        e.preventDefault();
                                        voiceActions.pausePlayback();
                                    }}
                                    aria-label="Pause playback"
                                >
                                    <Svg src={imageLinks.icon.pbPause} />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="playback-button"
                                    onClick={async (e) => {
                                        // Explicit onClick handler for React Native webview compatibility
                                        e.preventDefault();
                                        if (voiceState.currentState === VoiceRecorderState.RECORD_PAUSED) {
                                            voiceActions.playRecording(); // Let playRecording handle the current position
                                        } else {
                                            voiceActions.seekTo(0);
                                            voiceActions.playRecording();
                                        }
                                    }}
                                    aria-label={voiceState.currentState === VoiceRecorderState.RECORD_PAUSED ? "Resume playback" : "Play recording"}
                                >
                                    <Svg src={imageLinks.icon.playIcon} />
                                </button>
                            )}
                        </>

                        <div className="timer-content">

                            {/* <span className="status-text">{getStatusText()}</span> */}

                            {/* Progress bar - only show during playback */}
                            {voiceState.duration > 0 && voiceState.audioUrl && (
                                <WaveformVisualizer
                                    audioUrl={voiceState.audioUrl}
                                    isPlaying={voiceState.currentState === VoiceRecorderState.RECORD_PLAYING}
                                    onPlayPause={voiceActions.playRecording}
                                    onSeek={(progress) => {
                                        console.log("progress", progress);
                                        const newTime = Math.min(Math.max(0, progress * voiceState.duration), voiceState.duration);
                                        voiceActions.seekTo(newTime);
                                    }}
                                    duration={voiceState.duration}
                                    formattedDuration={voiceState.formattedDuration}
                                    currentProgress={voiceState.playbackDuration / voiceState.duration}
                                />
                            )}

                            <span className="duration-text">
                                {getTimerDisplay()}
                            </span>
                        </div>
                    </div>
                )}

                {/* Audio Visualizer - Show during recording states */}
                {!voiceState.isPaused && (
                    <div className={`audio-visualizer ${voiceState.currentState === VoiceRecorderState.RECORDING_PAUSED ? 'paused' : 'active'}`}>
                        {bars.map((height, index) => (
                            <div
                                key={index}
                                className="audio-bar"
                                style={{
                                    height: `${height * 4}px`,
                                    animationDelay: `${index * 0.1}s`
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="recording-controls">
                <>
                    {voiceState.isPaused && !voiceState.hasReachedDurationLimit ? (
                        <button
                            type="button"
                            className="resume-button"
                            onClick={(e) => {
                                // Explicit onClick handler for React Native webview compatibility
                                e.preventDefault();
                                voiceActions.resumeRecording();
                            }}
                            aria-label="Resume recording"
                        >
                            <Svg src={imageLinks.icon.micIcon} />
                        </button>
                    ) : !voiceState.isPaused ? (
                        <button
                            type="button"
                            className="pause-button"
                            onClick={(e) => {
                                // Explicit onClick handler for React Native webview compatibility
                                e.preventDefault();
                                voiceActions.pauseRecording();
                            }}
                            aria-label="Pause recording"
                        >
                            <Svg src={imageLinks.icon.pauseIcon} />
                        </button>
                    ) : null
                    }
                </>

                <LanguageSelector className="language-selector-control" />


                <button
                    type="button"
                    className="stop-button"
                    onClick={(e) => {
                        // Explicit onClick handler for React Native webview compatibility
                        e.preventDefault();
                        handleSubmit();
                    }}
                    aria-label={showPlaybackInterface ? "Send recording" : "Stop recording"}
                >
                    <Svg src={imageLinks.icon.sendIcon} />
                </button>
            </div>
        </div>
    );
};

export default VoiceRecordingInterface;